// Per-engine projection functions: each takes an engine-native schema-cache
// snapshot and produces a Resolver `SchemaSnapshot`. Splits out from the
// `schemaCacheAdapter` dispatcher to keep the file budget respected.
//
// Relation identity: every projector keys `tables` / `columns` by
// `relationKey(qualified)` (schema-qualified when the relation has a schema)
// and sets `defaultSchemas` — the schemas an unqualified reference resolves
// through (`snapshotRelations.ts`) — plus `defaultSchemasExact` when that list
// is the session's own, read from the server. Same-named relations in two
// schemas stay two relations, each with its own columns, FK edges and JSON
// declarations.
//
// Per-engine shape:
// - PG: projected from the schema-qualified `snap.tables` (never the legacy
//   bare-name `columnsByTableLower`, which merges same-named relations of
//   different schemas); `defaultSchemas` = the introspected search_path.
// - MSSQL: `defaultSchemas` = the database user's default schema
//   (`SCHEMA_NAME()`, introspected with the server info), then `dbo`.
// - MySQL: cache is per-database; `database` acts as schema for all entries.
// - SQLite: no schema concept; tables projected schemaless.
// - Oracle: each object carries its own schema; the snapshot holds ONE owner
//   (`OWNER = :schema`), which is the default schema.
// - MSSQL: tables/columns/FKs/routines all project from `mssqlTableCache`'s
//   `MssqlTableSnapshot` (bulk introspection already fetches all four — see
//   plan17 §W1); TVF parameter-shape sniffing is not attempted, so every
//   'function'-kind routine maps to 'scalar-function'.

import type { Column, FkEdge, SchemaSnapshot, Table } from '../types';
import type { PgSchemaSnapshot } from '@/features/connections/services/language-services/pgSchemaTypes';
import type {
  MysqlSchemaSnapshot,
  MysqlSchemaObject,
} from '@/features/connections/services/language-services/mysqlSchemaTypes';
import type {
  OracleSchemaSnapshot,
  OracleSchemaObject,
} from '@/features/connections/services/language-services/oracleSchemaTypes';
import type {
  SqliteSchemaSnapshot,
  SqliteSchemaObject,
} from '@/features/connections/services/language-services/sqliteSchemaTypes';
import type { MssqlTableSnapshot } from '@/features/connections/services/language-services/mssqlTableCache';

import {
  columnsFor,
  mergeFkEdgesByConstraint,
  foldFor,
  projectMssqlRoutines,
  projectRoutines,
  qn,
  type FoldFn,
} from './schemaProjectionHelpers';
import { relationKey } from './snapshotRelations';
import {
  detectMssqlIsJsonColumns,
  detectOracleIsJsonColumns,
  detectSqliteJsonValidColumns,
} from '../json/classify/checkIsJsonDetection';
import {
  lookupLooseJsonColumns,
  type LooseJsonHintIndex,
} from '../json/looseJsonHintIndex';

function projectMysqlObject(
  obj: MysqlSchemaObject,
  database: string,
  fold: FoldFn,
  looseJsonIndex: LooseJsonHintIndex | undefined,
): { table: Table; columns: Column[]; fks: FkEdge[] } {
  const qualified = qn(obj.name, database, fold);
  const table: Table = {
    qualified,
    kind: obj.type === 'view' ? 'view' : 'table',
    columnCount: obj.columns.length,
  };
  const fks: FkEdge[] = obj.foreignKeys.map((fk) => ({
    fromTable: qualified,
    fromColumns: [fk.columnName],
    toTable: qn(fk.referencedTable, database, fold),
    toColumns: [fk.referencedColumn],
    name: fk.constraintName,
  }));
  const looseJsonDeclaredColumns = lookupLooseJsonColumns(looseJsonIndex, qualified);
  // Columns see whole constraints (composite members are flagged); the
  // snapshot's edges stay one per row.
  const constraintFks = mergeFkEdgesByConstraint(fks, obj.foreignKeys.map((fk) => fk.constraintName));
  return {
    table,
    columns: columnsFor(qualified, obj.columns, fold, 'mysql', { looseJsonDeclaredColumns }, constraintFks),
    fks,
  };
}

function projectOracleObject(
  obj: OracleSchemaObject,
  fold: FoldFn,
  looseJsonIndex: LooseJsonHintIndex | undefined,
  fks: readonly FkEdge[],
): { table: Table; columns: Column[] } {
  const qualified = qn(obj.name, obj.schema, fold);
  const kind = obj.type === 'materialized_view' ? 'materialized-view' : obj.type === 'view' ? 'view' : 'table';
  const table: Table = { qualified, kind, columnCount: obj.columns.length };
  // Slice A3: route ALL_CONSTRAINTS.SEARCH_CONDITION through the IS JSON
  // detector. The richer per-hit `strict` flag survives at the detector
  // layer for later phases; the cascade router only needs the column-name set.
  // ALL_CONSTRAINTS.SEARCH_CONDITION preserves user-written case, while
  // ALL_TAB_COLUMNS.COLUMN_NAME is uppercase — fold detector hits to match
  // the projector's column.name case (Oracle identifiers fold uppercase).
  const isJsonHits = detectOracleIsJsonColumns(obj.checkConstraints);
  const isJsonCheckedColumns = isJsonHits.size === 0
    ? undefined
    : new Set<string>(Array.from(isJsonHits.keys(), (k) => k.toUpperCase()));
  const looseJsonDeclaredColumns = lookupLooseJsonColumns(looseJsonIndex, qualified);
  return {
    table,
    columns: columnsFor(qualified, obj.columns, fold, 'oracle', {
      isJsonCheckedColumns,
      looseJsonDeclaredColumns,
    }, fks),
  };
}

function projectSqliteObject(
  obj: SqliteSchemaObject,
  fold: FoldFn,
  looseJsonIndex: LooseJsonHintIndex | undefined,
): { table: Table; columns: Column[]; fks: FkEdge[] } {
  const qualified = qn(obj.name, undefined, fold);
  const table: Table = {
    qualified,
    kind: obj.type === 'view' ? 'view' : 'table',
    columnCount: obj.columns.length,
  };
  const fks: FkEdge[] = obj.foreignKeys.map((fk) => ({
    fromTable: qualified,
    fromColumns: [fk.from],
    toTable: qn(fk.referencedTable, undefined, fold),
    toColumns: [fk.to],
  }));
  // Slice A3: parse `sqlite_master.sql` for column-level CHECK (json_valid(col))
  // — already loaded into the schema cache, no extra introspection round-trip.
  const isJsonCheckedColumns = detectSqliteJsonValidColumns(obj.sql);
  const looseJsonDeclaredColumns = lookupLooseJsonColumns(looseJsonIndex, qualified);
  return {
    table,
    // Columns see whole constraints (rows sharing a pragma `id`), named
    // `fk_<id>` as Table Explorer's metadata names them (the pragma reports
    // no name) so both pick the same target among a column's FKs
    // (`compareFkConstraints`); the snapshot's edges stay one per row.
    columns: columnsFor(qualified, obj.columns, fold, 'sqlite', {
      isJsonCheckedColumns,
      looseJsonDeclaredColumns,
    }, mergeFkEdgesByConstraint(
      obj.foreignKeys.map((fk, i) => ({ ...fks[i]!, name: `fk_${fk.id}` })),
      obj.foreignKeys.map((fk) => String(fk.id)),
    )),
    fks,
  };
}

export function projectPgSchema(
  snap: PgSchemaSnapshot,
  looseJsonIndex?: LooseJsonHintIndex,
): SchemaSnapshot {
  const fold = foldFor('postgres');
  const tables = new Map<string, Table>();
  const columns = new Map<string, readonly Column[]>();
  // Computed BEFORE the columns loop (moved from after) so `columnsFor()` can
  // thread the FK data into each table's Column rows (PARITY-FK-01).
  const fks: FkEdge[] = snap.foreignKeys.map((fk) => ({
    fromTable: qn(fk.fromTable, fk.fromSchema, fold),
    fromColumns: Array.from(fk.fromColumns),
    toTable: qn(fk.toTable, fk.toSchema, fold),
    toColumns: Array.from(fk.toColumns),
    name: fk.name,
  }));
  for (const entry of snap.tables) {
    const qualified = qn(entry.name, entry.schema || undefined, fold);
    const key = relationKey(qualified);
    // `orders` and `"Orders"` fold to one key here (a bare reference folds to
    // lower case, and the resolver's identity is the fold). The one spelled
    // as its fold is what a bare `orders` names, so it keeps the key rather
    // than whichever the catalog listed last.
    const held = tables.get(key)?.qualified;
    if (held && spelledAsFolded(held) && !spelledAsFolded(qualified)) continue;
    tables.set(key, { qualified, kind: entry.kind ?? 'table', columnCount: entry.columns.length });
    const looseJsonDeclaredColumns = lookupLooseJsonColumns(looseJsonIndex, qualified);
    columns.set(
      key,
      columnsFor(qualified, entry.columns, fold, 'postgres', { looseJsonDeclaredColumns }, fks),
    );
  }
  // `searchPath` is `SHOW search_path` expanded and lower-cased. Read from
  // the server it is exact — even EMPTY, when no bare name resolves at all.
  // When the probe failed, PostgreSQL's own default is only a guess, so a
  // bare name may still resolve to the only relation of that name.
  const searchPath = snap.searchPath.map(fold);
  const searchPathKnown = snap.searchPathKnown ?? searchPath.length > 0;
  return {
    engineId: 'postgres',
    connectionId: snap.connectionId,
    database: snap.database,
    tables,
    columns,
    defaultSchemas: searchPathKnown ? searchPath : ['public'],
    ...(searchPathKnown ? { defaultSchemasExact: true } : {}),
    routines: projectRoutines(snap.routinesByNameLower, 'postgres'),
    foreignKeys: fks,
    fetchedAt: snap.builtAt,
  };
}

function spelledAsFolded(q: { name: string; foldedName: string; schema?: string; foldedSchema?: string }): boolean {
  return q.name === q.foldedName && (q.schema ?? '') === (q.foldedSchema ?? '');
}

export function projectMysqlSchema(
  snap: MysqlSchemaSnapshot,
  looseJsonIndex?: LooseJsonHintIndex,
): SchemaSnapshot {
  const fold = foldFor('mysql');
  const tables = new Map<string, Table>();
  const columns = new Map<string, readonly Column[]>();
  const fks: FkEdge[] = [];
  for (const obj of [...snap.tables, ...snap.views]) {
    const { table, columns: cols, fks: edges } = projectMysqlObject(obj, snap.database, fold, looseJsonIndex);
    tables.set(relationKey(table.qualified), table);
    columns.set(relationKey(table.qualified), cols);
    fks.push(...edges);
  }
  return {
    engineId: 'mysql',
    connectionId: snap.connectionId,
    database: snap.database,
    tables,
    columns,
    defaultSchemas: [fold(snap.database)],
    defaultSchemasExact: true,
    routines: projectRoutines(snap.routinesByNameLower, 'mysql'),
    foreignKeys: fks,
    fetchedAt: snap.builtAt,
  };
}

export function projectOracleSchema(
  snap: OracleSchemaSnapshot,
  looseJsonIndex?: LooseJsonHintIndex,
): SchemaSnapshot {
  const fold = foldFor('oracle');
  const tables = new Map<string, Table>();
  const columns = new Map<string, readonly Column[]>();
  // Computed BEFORE the columns loop (moved from after) so each
  // `projectOracleObject()` call can thread the FK data into that table's
  // Column rows (PARITY-FK-01).
  const fks: FkEdge[] = snap.foreignKeys.map((fk) => ({
    fromTable: qn(fk.fromTable, fk.fromSchema, fold),
    fromColumns: Array.from(fk.fromColumns),
    toTable: qn(fk.toTable, fk.toSchema, fold),
    toColumns: Array.from(fk.toColumns),
    name: fk.name,
  }));
  for (const obj of [...snap.tables, ...snap.views]) {
    const { table, columns: cols } = projectOracleObject(obj, fold, looseJsonIndex, fks);
    tables.set(relationKey(table.qualified), table);
    columns.set(relationKey(table.qualified), cols);
  }
  return {
    engineId: 'oracle',
    connectionId: snap.connectionId,
    database: snap.schema,
    tables,
    columns,
    defaultSchemas: [fold(snap.schema)],
    defaultSchemasExact: true,
    routines: projectRoutines(snap.routinesByNameLower, 'oracle'),
    foreignKeys: fks,
    fetchedAt: snap.builtAt,
  };
}

export function projectSqliteSchema(
  snap: SqliteSchemaSnapshot,
  looseJsonIndex?: LooseJsonHintIndex,
): SchemaSnapshot {
  const fold = foldFor('sqlite');
  const tables = new Map<string, Table>();
  const columns = new Map<string, readonly Column[]>();
  const fks: FkEdge[] = [];
  for (const obj of [...snap.tables, ...snap.views]) {
    const { table, columns: cols, fks: edges } = projectSqliteObject(obj, fold, looseJsonIndex);
    tables.set(relationKey(table.qualified), table);
    columns.set(relationKey(table.qualified), cols);
    fks.push(...edges);
  }
  return {
    engineId: 'sqlite',
    connectionId: snap.connectionId,
    database: snap.database,
    tables,
    columns,
    routines: new Map(),
    foreignKeys: fks,
    fetchedAt: snap.builtAt,
  };
}

/**
 * SQL Server resolves an unqualified name in the database user's default
 * schema, then in `dbo`. Without the probe (it failed) `dbo` is a guess —
 * every user without a DEFAULT_SCHEMA has it — so it is not exact.
 */
function mssqlDefaultSchemas(
  defaultSchema: string | undefined,
  fold: FoldFn,
): Pick<SchemaSnapshot, 'defaultSchemas' | 'defaultSchemasExact'> {
  if (!defaultSchema) return { defaultSchemas: ['dbo'] };
  const own = fold(defaultSchema);
  return { defaultSchemas: own === 'dbo' ? ['dbo'] : [own, 'dbo'], defaultSchemasExact: true };
}

export function projectMssqlTables(
  snap: MssqlTableSnapshot,
  looseJsonIndex?: LooseJsonHintIndex,
): SchemaSnapshot {
  const fold = foldFor('mssql');
  const tables = new Map<string, Table>();
  const columns = new Map<string, readonly Column[]>();
  // Computed BEFORE the tables loop (moved from after) so `columnsFor()` can
  // thread the FK data into each table's Column rows (PARITY-FK-01).
  const fks: FkEdge[] = snap.foreignKeys.map((fk) => ({
    fromTable: qn(fk.fromTable, fk.fromSchema, fold),
    fromColumns: Array.from(fk.fromColumns),
    toTable: qn(fk.toTable, fk.toSchema, fold),
    toColumns: Array.from(fk.toColumns),
    name: fk.name,
  }));
  for (const entry of snap.tables) {
    const qualified = qn(entry.name, entry.schema, fold);
    const tableMeta: Table = entry.columns
      ? {
          qualified,
          kind: entry.type === 'view' ? 'view' : 'table',
          columnCount: entry.columns.length,
        }
      : {
          qualified,
          kind: entry.type === 'view' ? 'view' : 'table',
        };
    const key = relationKey(qualified);
    tables.set(key, tableMeta);
    // Slice A3.5: when bulk-mode introspection populated columns + CHECK
    // clauses, route the clauses through the IS JSON detector. The detector
    // already handles `CHECK ( … )` wrappers, bracket / quoted identifier
    // unwrapping, and compound-clause rejection.
    if (entry.columns && entry.columns.length > 0) {
      const isJsonHits = detectMssqlIsJsonColumns(entry.checkConstraints);
      const isJsonCheckedColumns = isJsonHits.size === 0 ? undefined : isJsonHits;
      const looseJsonDeclaredColumns = lookupLooseJsonColumns(looseJsonIndex, qualified);
      // Fidelity fix: `MssqlTableColumnEntry.nullable` structurally mismatches
      // `columnsFor`'s `ColumnLike.isNullable` — without this mapping every
      // mssql column silently projects `nullable: false` (plan17 §W1 recon).
      const columnsWithNullableFidelity = entry.columns.map((c) => ({
        ...c,
        isNullable: c.nullable,
      }));
      columns.set(
        key,
        columnsFor(qualified, columnsWithNullableFidelity, fold, 'mssql', {
          isJsonCheckedColumns,
          looseJsonDeclaredColumns,
        }, fks),
      );
    }
  }
  return {
    engineId: 'mssql',
    connectionId: snap.connectionId,
    database: snap.database,
    tables,
    columns,
    ...mssqlDefaultSchemas(snap.defaultSchema, fold),
    routines: projectMssqlRoutines(snap.routines),
    foreignKeys: fks,
    fetchedAt: snap.builtAt,
  };
}
